/**
 * `/zadania/rodzaje` — which kinds of task reach the trainer's queue (R16 to R19).
 *
 * A screen of its own under the cog, not a drawer (owner, 18 September 2026), with a crumb back to
 * `/zadania`. Three group cards in the backend's own order, a switch on every row, and one sentence
 * saying where the setting lives.
 *
 * ── It is the SAME setting the phone writes ──────────────────────────────────────────────────
 * `AttentionPreferences` is stored per user and the queue endpoint skips a muted kind's detector on
 * the server, so a kind turned off here disappears from the phone's deck, the Pulpit's bento, the
 * roster's risk lines and this screen's own queue. The sentence at the top says so, because a
 * setting that reaches further than the screen it is on has to say that it does.
 *
 * ── Low sleep and low energy carry a LEVEL (R25) ─────────────────────────────────────────────
 * A stepper under the description, always there (`docs/design/zadania-progi-2026-10-02/`, variant
 * A): each step writes at once, like the switch — `KindLevel`.
 *
 * ── A refusal is drawn on the ROW and announced ──────────────────────────────────────────────
 * This app has no toasts and is not getting one. The switch goes back to where it was, mobile's own
 * `clients.taskPrefs.toggleFail` goes through the live region, and the same fact is printed under
 * the row the trainer is looking at. A level's refusal names the level that is in force.
 */

import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { ReasonTile, vitalKindOf, vitalSpelling } from '~/features/attention-queue';
import { screenMotion, useScreenMotionPreference } from '~/shared/design-system/motion';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import { useDocumentTitle } from '~/shared/lib/useDocumentTitle';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import shell from '~/shared/ui/canonScreen.module.css';
import { EmptyState } from '~/shared/ui/EmptyState';
import { HintPopover } from '~/shared/ui/HintPopover';
import { InboxMarkIcon } from '~/shared/ui/EmptyStateIcons';
import ghost from '~/shared/ui/ghostButton.module.css';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import skel from '~/shared/ui/skeleton.module.css';

import { useTaskKinds } from '../lib/useTaskKinds';
import type { TaskKindRow } from '../model/taskKinds';
import { KindLevel } from './KindLevel';
import { KindSwitch } from './KindSwitch';
import { BackIcon } from './TaskIcons';
import styles from './taskKinds.module.css';

/** How many skeleton rows a group card reserves while the catalogue is on its way. */
const SKELETON_ROWS = 5;
const SKELETON_CARDS = 3;

export function TaskKindsScreen() {
  const { t, i18n } = useTranslation();
  const announcer = useAnnouncer();
  const reducedMotion = useScreenMotionPreference();
  const kinds = useTaskKinds(announcer.announce);
  const gate = useSkeletonGate(kinds.status === 'pending');
  /**
   * The screen's wait branch, which is the GATE's verdict and not the read's. The two differ by the
   * skeleton's 300ms minimum life, and forking on `kinds.status` flashed the cards in at full
   * opacity the moment the catalogue answered and blinked them out and back at the hand-over.
   */
  const status = gate.waiting ? 'pending' : kinds.status;

  useDocumentTitle(t('web:tasks.kinds.documentTitle'));

  return (
    <main className={shell.page}>
      <motion.div className={`${shell.measure} ${styles.page}`} {...screenMotion('rise', { reducedMotion })}>
        <Link className={styles.crumb} to="/zadania" aria-label={t('clients.taskPrefs.a11y.back')}>
          <BackIcon className={styles.crumbGlyph} />
          {t('web:tasks.kinds.back')}
        </Link>

        {/* `AppShell` moves focus here on arrival, so it renders in every state. */}
        <h1 className={styles.title}>{t('clients.taskPrefs.title')}</h1>
        {/* The whole explanation lives behind the ⓘ — what muting a type actually does, what it
          * does NOT do, and where the setting is kept. The owner's standing rule since the
          * progression round: an explanation lives under an ⓘ or it goes. Three sentences of it
          * were standing permanently over the three cards, which is most of what made this screen
          * read heavy; none of it is information a trainer needs on every visit. */}
        <p className={styles.sub}>
          {status === 'ready'
            ? `${t('web:tasks.kinds.counter', { on: kinds.kinds.on, total: kinds.kinds.total })} · ${t('web:tasks.kinds.lede')}`
            : t('web:tasks.kinds.lede')}
          <HintPopover label={t('clients.taskPrefs.dropdownLabel')}>
            {`${t('clients.taskPrefs.consequence')} ${t('clients.taskPrefs.netLine')} ${t('web:tasks.kinds.sync')}`}
          </HintPopover>
        </p>

        <LiveRegionAnnouncer message={announcer.message} />

        {status === 'error' ? (
          <div className={styles.state}>
            <EmptyState mark={<InboxMarkIcon />} tone="neutral" title={t('web:tasks.kinds.error')} />
            <button className={`${ghost.ghost} ${styles.retry}`} type="button" onClick={kinds.retry}>
              {t('web:clients.states.retry')}
            </button>
          </div>
        ) : status === 'empty' ? (
          /* The read SUCCEEDED and there is nothing to draw: `model/taskKinds` drops every reason
            * and every category this build has no words for, so a server that has shipped a
            * catalogue newer than this panel answers 200 with nothing it can name. A titled screen
            * over an empty scroller said nothing at all about why. */
          <div className={styles.state}>
            <EmptyState
              mark={<InboxMarkIcon />}
              tone="neutral"
              title={t('web:tasks.kinds.emptyTitle')}
              hint={t('web:tasks.kinds.emptyHint')}
            />
          </div>
        ) : (
          <div className={styles.scroller}>
            {/* The gate's hand-over: the cards that replace the bars carry it in their first
              * render, so the three columns fade in rather than cutting in. It is empty unless a
              * skeleton was actually drawn. */}
            <div className={`${styles.cols} ${gate.reveal}`}>
              {status === 'pending'
                ? Array.from({ length: SKELETON_CARDS }, (_, card) => (
                    <div className={styles.card} key={card}>
                      <p className={styles.groupHead} />
                      {Array.from({ length: SKELETON_ROWS }, (_, row) => (
                        <span className={`${styles.barRow} ${gate.skeleton ? skel.bar : ''}`} key={row} />
                      ))}
                    </div>
                  ))
                : kinds.kinds.groups.map((group) => (
                    <section className={styles.card} key={group.category} aria-label={t(group.labelKey)}>
                      <h2 className={styles.groupHead}>
                        {t(group.labelKey)}
                        <span className={styles.groupCount}>
                          {t('web:tasks.kinds.groupCounter', { on: group.on, total: group.total })}
                        </span>
                      </h2>
                      {group.rows.map((row) => (
                        <div
                          className={`${styles.row} ${row.muted ? styles.rowMuted : ''}`}
                          key={row.reason}
                        >
                          {/* The Pulpit list's tile, as on the queue (owner, 25 September 2026). */}
                          <ReasonTile
                            item={{ reason: row.reason, glyph: row.glyph, titleKey: row.nameKey }}
                            size="xl"
                            wash
                            decorative
                            className={styles.tile}
                          />
                          <div className={styles.rowText}>
                            <p className={styles.name}>{t(row.nameKey)}</p>
                            <p className={styles.desc}>{t(row.descKey)}</p>
                            {levelOf(row)}
                            {kinds.refused?.wireReason === row.wireReason ? (
                              <p className={styles.rowError}>{kinds.refused.sentence}</p>
                            ) : null}
                          </div>
                          <KindSwitch
                            name={t(row.nameKey)}
                            on={!row.muted}
                            onChange={(on) => {
                              kinds.setMuted(row, !on, () => t('clients.taskPrefs.toggleFail', { name: t(row.nameKey) }));
                            }}
                          />
                        </div>
                      ))}
                    </section>
                  ))}
            </div>
          </div>
        )}
      </motion.div>
    </main>
  );

  /** A kind's level under its description (R25) — only low sleep and low energy carry one. */
  function levelOf(row: TaskKindRow) {
    const kind = vitalKindOf(row.reason);
    if (row.level === null || kind === null) return null;
    const spelling = vitalSpelling(kind, (key, values) => t(key, values), i18n.language);
    const level = row.level;
    // A refused write names the level in force — the row as the server holds it, which the hook
    // hands over once the refused press is off the screen.
    const refusal = (held: TaskKindRow) =>
      t('web:tasks.kinds.level.saveFailed', { value: spelling.level((held.level ?? level).value) });
    return (
      <KindLevel
        name={t(row.nameKey)}
        level={level}
        muted={row.muted}
        spelling={spelling}
        onChange={(value) => {
          kinds.setLevel(row, value, refusal);
        }}
        onReset={() => {
          kinds.resetLevel(row, refusal);
        }}
      />
    );
  }
}
